import { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { Box, Typography } from '@mui/material'
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined'
import EmptyState from '../../components/Shared/EmptyState'
import { useTranslation } from 'react-i18next'
import {
  IHIDUser,
  IQueryUserVars,
  IQueryUserData,
  QUERY_USER_LIST,
} from '../../graphql/hid/queryUser'
import FullScreenLoader from '../../components/Shared/FullScreenLoader'
import { useAppSelector } from '../../redux/store'
import BaseDrawer from '../../components/Shared/BaseDrawer'
import HIDUsersTable from '../../components/HID/HIDUsersTable'
import { IUserDataRow } from '../../components/HID/HIDUsersTable'
import HIDUserDetails from '../../components/HID/HIDUserDetails'
import {
  GET_HID_CONFIG,
  IGetHIDConfigData,
  IGetHIDConfigVars,
} from '../../graphql/hid/getHIDConfig'
import { OrigoEventName } from '../../graphql/hid/getOrigoAccount'
import { colors } from '../../const/colors'
import HIDServiceData from '../../components/HID/HIDServiceData'
import HIDFilters from '../../components/HID/HIDFilters'

export const deletedStatuses = [
  OrigoEventName.USER_DELETED,
  OrigoEventName.USER_DELETE_INITIATED,
]

const HIDUsersList = () => {
  const { t } = useTranslation(['columns', 'hid', 'admin_menu'])
  const { tenantName } = useAppSelector(state => state.user)
  const [openUserDetails, setOpenUserDetails] = useState<IUserDataRow>()
  const [hidAccountFilter, setHidAccountFilter] = useState('')
  const [activeCardFilter, setActiveCardFilter] = useState('')
  const [filteredUsers, setFilteredUsers] = useState<IHIDUser[]>([])

  const filterHidAccounts = (items: IHIDUser[], isAccount: boolean) => {
    const filtered = isAccount
      ? items.filter(({ origoAccounts }) => !!origoAccounts.length)
      : items.filter(({ origoAccounts }) => !origoAccounts.length)
    return filtered
  }

  const filterHidCards = (items: IHIDUser[], isActive: boolean) => {
    const filtered = isActive
      ? items.filter(
          ({ origoAccounts }) => !!origoAccounts[0]?.origoCards[0]?.deviceId,
        )
      : items.filter(
          ({ origoAccounts }) => !origoAccounts[0]?.origoCards[0]?.deviceId,
        )
    return filtered
  }

  const { data, loading, refetch } = useQuery<IQueryUserData, IQueryUserVars>(
    QUERY_USER_LIST,
    {
      fetchPolicy: 'no-cache',
      variables: {
        tenant: tenantName,
      },
    },
  )

  const { data: tenant } = useQuery<IGetHIDConfigData, IGetHIDConfigVars>(
    GET_HID_CONFIG,
    {
      fetchPolicy: 'no-cache',
      variables: {
        name: tenantName,
      },
    },
  )

  useEffect(() => {
    if (data) {
      let items = data.queryUser
      if (!!hidAccountFilter) {
        hidAccountFilter === 'yes'
          ? (items = filterHidAccounts(items, true))
          : (items = filterHidAccounts(items, false))
      }
      if (!!activeCardFilter) {
        activeCardFilter === 'yes'
          ? (items = filterHidCards(items, true))
          : (items = filterHidCards(items, false))
      }
      setFilteredUsers(items)
    }
  }, [data, hidAccountFilter, activeCardFilter])

  if (loading || !data) {
    return <FullScreenLoader />
  }

  if (!tenant?.getTenant.hidConfig) {
    return (
      <EmptyState
        icon={<PhoneAndroidOutlinedIcon sx={{ fontSize: 34, color: colors.slate }} />}
        title={t('hid:no_hid_account_assigned')}
        description={t('hid:no_hid_account_assigned_desc')}
      />
    )
  }

  return (
    <>
      <Box sx={{ mb: '24px' }}>
        <Typography
          sx={{
            fontSize: '12px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#4F8F52',
            fontWeight: 800,
          }}
        >
          HID
        </Typography>
        <Typography
          sx={{
            fontSize: '30px',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            mt: '6px',
            mb: '4px',
            color: colors.navy,
          }}
        >
          {t('admin_menu:mobile_access')}
        </Typography>
        <Typography
          sx={{
            fontSize: '14.5px',
            color: '#6d8294',
          }}
        >
          {t('admin_menu:users_list')}
        </Typography>
      </Box>
      <HIDServiceData />
      <HIDUsersTable
        users={filteredUsers}
        setOpenUserDetails={setOpenUserDetails}
        refetch={refetch}
      >
        <HIDFilters
          hasAccount={hidAccountFilter}
          setHasAccount={setHidAccountFilter}
          activeCard={activeCardFilter}
          setActiveCard={setActiveCardFilter}
        />
      </HIDUsersTable>
      <BaseDrawer
        open={!!openUserDetails}
        width={552}
        background={colors.bg}
        disablePadding
      >
        {!!openUserDetails && (
          <HIDUserDetails
            userData={openUserDetails}
            closeDrawer={() => setOpenUserDetails(undefined)}
            listRefetch={refetch}
            hidConfig={tenant?.getTenant?.hidConfig}
          />
        )}
      </BaseDrawer>
    </>
  )
}

export default HIDUsersList
